
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { briefcaseOutline } from 'ionicons/icons';

interface WorkExperience {
  company: string;
  logo?: string;
  position: string;
  startDate: string;
  endDate: string;
  location: string;
  description: string;
  technologies?: string[];
  highlights?: string[];
  expanded?: boolean;
}

interface Position {
  title: string;
  startDate: string;
  endDate: string;
  description: string;
  technologies?: string[];
  highlights?: string[];
  expanded?: boolean;
}

interface GroupedCompany {
  name: string;
  logo?: string;
  location: string;
  totalDuration: string;
  earliestStart: string;
  latestEnd: string;
  positions: Position[];
  allTechnologies: string[];
}

interface ExperienceData {
  experiences: WorkExperience[];
}

export default defineComponent({
  name: 'LocalWorkExperience',
  props: {
    apiUrl: {
      type: String,
      default: '/data/work-experience.json'
    },
    branding: {
      type: Boolean,
      default: true
    },
    columns: {
      type: Number,
      default: 0 // 0 = auto-responsive
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const experiences = ref<WorkExperience[]>([]);
    const groupedCompanies = ref<GroupedCompany[]>([]);
    const windowWidth = ref(window.innerWidth);

    // Computed properties
    const showBranding = computed(() => props.branding);

    const gridColumns = computed(() => {
      if (props.columns > 0) {
        return props.columns;
      }

      // Responsive column calculation
      if (windowWidth.value >= 1200) {
        return 2;
      } else if (windowWidth.value >= 768) {
        return 2;
      } else {
        return 1;
      }
    });

    const formatDateRange = (startDate: string, endDate: string): string => {
      const start = new Date(startDate);
      const end = endDate.toLowerCase() === 'present' ? null : new Date(endDate);

      const startStr = start.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });

      const endStr = end ? end.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      }) : 'Present';

      return `${startStr} - ${endStr}`;
    };

    const formatDuration = (experience: WorkExperience): string => {
      const start = new Date(experience.startDate);
      const end = experience.endDate.toLowerCase() === 'present'
        ? new Date()
        : new Date(experience.endDate);

      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));

      if (diffMonths < 12) {
        return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
      } else {
        const years = Math.floor(diffMonths / 12);
        const remainingMonths = diffMonths % 12;

        let result = `${years} year${years !== 1 ? 's' : ''}`;
        if (remainingMonths > 0) {
          result += ` ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
        }
        return result;
      }
    };

    const formatDescription = (description: string): string[] => {
      // Split description into paragraphs
      return description.split('\n').filter(p => p.trim().length > 0);
    };

    const shouldShowExpand = (description: string): boolean => {
      // Simple heuristic: if content is long enough that it would likely be truncated to 3 lines
      // This ensures all truncated content gets a "Show More" button
      return description.length > 180; // Roughly 3 lines at ~60 chars per line
    };

    const truncateWithEllipsis = (text: string, maxLength = 180): string => {
      if (text.length <= maxLength) return text;

      // Find the last complete word before the limit
      const truncated = text.substring(0, maxLength);
      const lastSpaceIndex = truncated.lastIndexOf(' ');

      if (lastSpaceIndex > maxLength * 0.8) { // Only use word boundary if it's not too far back
        return truncated.substring(0, lastSpaceIndex) + '...';
      }

      return truncated + '...';
    };

    const toggleExpand = (experience: WorkExperience) => {
      experience.expanded = !experience.expanded;
    };

    const formatCompanyName = (companyName: string): string => {
      // Remove "Full-time" suffix and clean up company names for grouping
      return companyName.replace(/\s+Full-time$/i, '').trim();
    };

    const calculateTotalDuration = (positions: Position[]): string => {
      if (positions.length === 0) return '';

      // Find earliest start and latest end - make a copy to avoid mutating original array
      const sortedPositions = [...positions].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
      const earliestStart = new Date(sortedPositions[0].startDate);
      const latestEnd = sortedPositions[sortedPositions.length - 1].endDate.toLowerCase() === 'present'
        ? new Date()
        : new Date(sortedPositions[sortedPositions.length - 1].endDate);

      const diffTime = Math.abs(latestEnd.getTime() - earliestStart.getTime());
      const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));

      if (diffMonths < 12) {
        return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
      } else {
        const years = Math.floor(diffMonths / 12);
        const remainingMonths = diffMonths % 12;

        let result = `${years} year${years !== 1 ? 's' : ''}`;
        if (remainingMonths > 0) {
          result += ` ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
        }
        return result;
      }
    };

    const mergeTechnologies = (positions: Position[]): string[] => {
      const allTech = new Set<string>();
      positions.forEach(position => {
        if (position.technologies) {
          position.technologies.forEach(tech => allTech.add(tech));
        }
      });
      return Array.from(allTech);
    };

    const groupExperiencesByCompany = (experiences: WorkExperience[]): GroupedCompany[] => {
      const companyMap = new Map<string, WorkExperience[]>();

      // Group experiences by cleaned company name
      experiences.forEach(exp => {
        const cleanName = formatCompanyName(exp.company);
        if (!companyMap.has(cleanName)) {
          companyMap.set(cleanName, []);
        }
        const companyExps = companyMap.get(cleanName);
        if (companyExps) {
          companyExps.push(exp);
        }
      });

      // Convert to GroupedCompany objects and sort by most recent position start date
      const groupedCompanies = Array.from(companyMap.entries()).map(([companyName, companyExps]) => {
        // Sort positions by start date (most recent first)
        const sortedExps = companyExps.sort((a, b) => {
          // Ensure proper date parsing by adding -01 if needed
          const dateA = new Date(a.startDate.includes('-') && a.startDate.split('-').length === 2 ? `${a.startDate}-01` : a.startDate);
          const dateB = new Date(b.startDate.includes('-') && b.startDate.split('-').length === 2 ? `${b.startDate}-01` : b.startDate);
          return dateB.getTime() - dateA.getTime();
        });

        // Convert to Position objects
        const positions: Position[] = sortedExps.map(exp => {
          // Expand Pocket Network, Fetch.ai, Storj Labs, and Trucoin by default
          const shouldExpandByDefault = ['Pocket Network', 'Fetch.ai', 'Storj Labs', 'Trucoin'].some(
            company => companyName.toLowerCase().includes(company.toLowerCase())
          );

          return {
            title: exp.position,
            startDate: exp.startDate,
            endDate: exp.endDate,
            description: exp.description,
            technologies: exp.technologies,
            highlights: exp.highlights,
            expanded: shouldExpandByDefault
          };
        });

        // Use the most recent position for company-level info
        const mostRecent = sortedExps[0];
        const earliestStart = sortedExps[sortedExps.length - 1].startDate;
        const latestEnd = mostRecent.endDate;

        return {
          name: companyName,
          logo: mostRecent.logo,
          location: mostRecent.location,
          totalDuration: calculateTotalDuration(positions),
          earliestStart,
          latestEnd,
          positions,
          allTechnologies: mergeTechnologies(positions)
        };
      });

      // Sort companies by most recent position start date (most recent first)
      return groupedCompanies.sort((a, b) => {
        // Ensure proper date parsing by adding -01 if needed
        const aStartDate = a.positions[0].startDate;
        const bStartDate = b.positions[0].startDate;
        const aMostRecent = new Date(aStartDate.includes('-') && aStartDate.split('-').length === 2 ? `${aStartDate}-01` : aStartDate);
        const bMostRecent = new Date(bStartDate.includes('-') && bStartDate.split('-').length === 2 ? `${bStartDate}-01` : bStartDate);
        return bMostRecent.getTime() - aMostRecent.getTime();
      });
    };

    const updateWindowWidth = () => {
      windowWidth.value = window.innerWidth;
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        const response = await fetch(props.apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to load experience data: ${response.status}`);
        }

        const data: ExperienceData = await response.json();

        // Initialize expanded state
        experiences.value = data.experiences.map(exp => ({
          ...exp,
          expanded: false
        }));

        // Group experiences by company
        groupedCompanies.value = groupExperiencesByCompany(experiences.value);

      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load experience data';
        console.error('Work Experience Data Error:', err);
      } finally {
        loading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      fetchData();

      const handleResize = () => updateWindowWidth();
      window.addEventListener('resize', handleResize);

      onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
      });
    });

    const togglePositionExpand = (position: Position) => {
      position.expanded = !position.expanded;
    };

    const getTechCategory = (technology: string): string => {
      // Use the same mapping as LocalTechnologyTimeline for consistency
      const techMapping: Record<string, string> = {
        // Languages
        'go': 'language',
        'python': 'language',
        'javascript': 'language',
        'typescript': 'language',
        'rust': 'language',
        'solidity': 'language',
        'c': 'language',
        'dart': 'language',
        'ruby': 'language',
        'java': 'language',
        'cpp': 'language',
        'c++': 'language',

        // Frameworks
        'angular': 'framework',
        'angularjs': 'framework',
        'react': 'framework',
        'nextjs': 'framework',
        'vue': 'framework',
        'svelte': 'framework',
        'express': 'framework',
        'fastapi': 'framework',
        'django': 'framework',
        'flutter': 'framework',
        'electron': 'framework',
        'meteorjs': 'framework',
        'ruby on rails': 'framework',
        'rails': 'framework',
        'wordpress': 'framework',
        'cosmwasm': 'framework',
        'capacitor': 'framework',
        'create-react-app': 'framework',
        'nodejs': 'framework',
        'redux': 'framework',
        'apollo': 'framework',
        'postgraphile': 'framework',
        'subquery': 'framework',
        'ignite': 'framework',

        // Infrastructure & DevOps
        'docker': 'infrastructure',
        'kubernetes': 'infrastructure',
        'k8s': 'infrastructure',
        'terraform': 'infrastructure',
        'helm': 'infrastructure',
        'ansible': 'infrastructure',
        'tilt': 'infrastructure',
        'aws': 'infrastructure',
        'gcp': 'infrastructure',
        'azure': 'infrastructure',
        'linux': 'infrastructure',
        'opentelemetry': 'infrastructure',
        'signoz': 'infrastructure',
        'system-administration': 'infrastructure',

        // Databases
        'postgresql': 'database',
        'postgres': 'database',
        'mongodb': 'database',
        'influxdb': 'database',
        'cockroachdb': 'database',
        'redis': 'database',
        'mysql': 'database',
        'sqlite': 'database',

        // Security
        'afl': 'security',
        'gofuzz': 'security',
        'go-fuzz': 'security',
        'js-fuzz': 'security',
        'libfuzzer': 'security',
        'electronegativity': 'security',
        'fuzz testing': 'security',
        'static analysis': 'security',
        'code review': 'security',
        'bls12-381': 'security',
        'smst': 'security',

        // Blockchain
        'cosmos-sdk': 'blockchain',
        'ethereum': 'blockchain',
        'bitcoin': 'blockchain',
        'tendermint': 'blockchain',
        'cometbft': 'blockchain',
        'celestia': 'blockchain',
        'polygon': 'blockchain',
        'rollkit': 'blockchain',
        'agoric': 'blockchain',
        'axelar': 'blockchain',
        'osmosis': 'blockchain',
        'op-stack': 'blockchain',
        'interchain-accounts': 'blockchain',
        'packet-forward-middleware': 'blockchain',

        // Protocols
        'protobuf': 'protocol',
        'grpc': 'protocol',
        's/kademlia': 'protocol',
        'kademlia': 'protocol',
        'gossipsub': 'protocol',
        'libp2p': 'protocol',
        'graphql': 'protocol',
        'ibc': 'protocol',
        'magic-wormhole': 'protocol',

        // Language Interoperability
        'webassembly': 'interop',
        'emscripten': 'interop',
        'swig': 'interop',
        'cmake': 'interop',

        // Tools & Other
        'cli': 'other',
        'css': 'other',
        'html': 'other',
        'networking': 'other',
        'selenium': 'other',
        'serialtty': 'other',
        'vnc': 'other',
        'vpn': 'other',
        'web-apis': 'other',
        'disaster recovery': 'other',
        'e-commerce': 'other',
        'windows': 'other',
      };

      // Normalize the technology name and look it up
      const normalizedTech = technology.toLowerCase().replace(/[^a-z0-9]/g, '');
      const techLower = technology.toLowerCase();

      // Try exact match first
      if (techMapping[techLower]) {
        return 'tech-' + techMapping[techLower];
      }

      // Try normalized match
      if (techMapping[normalizedTech]) {
        return 'tech-' + techMapping[normalizedTech];
      }

      // Check for partial matches (for compound terms).
      // Guard: only substring-match keys >= 3 chars so short keys like
      // 'c'/'go' can't greedily mislabel (e.g. cockroachdb -> language).
      for (const [key, category] of Object.entries(techMapping)) {
        const nkey = key.replace(/[^a-z0-9]/g, '');
        if (key.length >= 3 && techLower.includes(key)) {
          return 'tech-' + category;
        }
        if (nkey.length >= 3 && normalizedTech.includes(nkey)) {
          return 'tech-' + category;
        }
      }

      // Default category
      return 'tech-other';
    };

    return {
      briefcaseOutline,
      loading,
      error,
      experiences,
      groupedCompanies,
      showBranding,
      gridColumns,
      formatDateRange,
      formatDuration,
      formatDescription,
      shouldShowExpand,
      truncateWithEllipsis,
      toggleExpand,
      togglePositionExpand,
      getTechCategory
    };
  }
});
